/**
 * 「差異是不是漏勾了某幾筆?」的比對(REQ-20260908002 運費歸單畫面)。
 *
 * 使用者勾完明細、輸入廠商對帳單金額之後,差異多半是**漏勾了一兩趟**。
 * 與其讓人自己一列一列加,系統直接在**未勾選**的明細裡找出加起來剛好等於差異的組合。
 *
 * ⚠️ **只做到兩筆**。三筆以上是子集合和問題(NP-hard),資料量一大就是指數級搜尋;
 * 而且候選一多,「系統說可能是這幾筆」本身就不再是有用的提示。
 * 找不到時畫面只顯示差異值,由使用者自己判斷(那也是原本就有的資訊)。
 *
 * 全程以**分(整數)**運算 —— 0.1 + 0.2 那類浮點誤差會讓「剛好等於」永遠不成立。
 */

export interface MatchableLine {
  id: number;
  /** 未稅金額(字串)。到付 / 還沒填的是 null,不參與比對 */
  amount: string | null;
}

export interface DifferenceMatch {
  /** 湊出這個差異的明細 id(1 或 2 筆) */
  ids: number[];
}

/** 一次最多提示幾組候選 —— 再多就不是提示而是另一張表了 */
const MAX_MATCHES = 5;

function toCents(v: string | null): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/**
 * 在 `rows`(未勾選的明細)裡找出「單筆等於 `difference`」或
 * 「兩筆相加等於 `difference`」的組合。差異是 0、或沒有候選時回空陣列。
 *
 * 兩筆的搜尋是 O(n²) 的雙迴圈 —— 一家貨運行一個月的班次量級(數十筆)完全吃得下,
 * 而且它有明確的上界,不會因為資料變多就變成指數級。
 */
export function matchDifference(
  difference: string | number,
  rows: MatchableLine[],
): DifferenceMatch[] {
  const target = Math.round(Number(difference) * 100);
  if (!Number.isFinite(target) || target === 0) return [];

  const usable = rows
    .map((r) => ({ id: r.id, cents: toCents(r.amount) }))
    .filter((r): r is { id: number; cents: number } => r.cents != null && r.cents > 0);

  const matches: DifferenceMatch[] = [];
  // 單筆優先 —— 「就是這一趟漏勾了」比「這兩趟加起來」好懂得多
  for (const r of usable) {
    if (r.cents === target) matches.push({ ids: [r.id] });
    if (matches.length >= MAX_MATCHES) return matches;
  }
  for (let i = 0; i < usable.length; i += 1) {
    for (let j = i + 1; j < usable.length; j += 1) {
      const a = usable[i];
      const b = usable[j];
      if (a && b && a.cents + b.cents === target) matches.push({ ids: [a.id, b.id] });
      if (matches.length >= MAX_MATCHES) return matches;
    }
  }
  return matches;
}
